import { useAuth } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { clerkAuth, type ClerkAuthResponse } from '~api/authentication';
import { ApiError } from '~types/api';
import { getApiErrorText } from '~utils/getApiErrorText';
import { callErrorToast } from '~utils/toasts';

interface UseClerkAuthPayload {
	onSuccess?: (data: ClerkAuthResponse | null) => void;
}

export const useClerkAuth = ({ onSuccess }: UseClerkAuthPayload) => {
	const { isLoaded, getToken } = useAuth();

	const { data, error, isLoading, refetch } = useQuery<
		unknown,
		ApiError,
		ClerkAuthResponse | null
	>({
		queryKey: ['clerk/session'],
		queryFn: async () => {
			const clerkToken = await getToken();
			if (clerkToken) {
				const { data } = await clerkAuth({ token: clerkToken });
				return data;
			}
			return null;
		},
		onSuccess: (data) => {
			onSuccess?.(data);
		},
		onError: (error) => {
			Sentry.captureException(error);
			callErrorToast({
				content: getApiErrorText(
					error,
					'An error occurred while fetching user session details.',
				),
			});
		},
		enabled: isLoaded,
	});

	return {
		session: data,
		isLoading,
		error: getApiErrorText(error),
		refetch,
	};
};
